import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { EzLogo, NavMobileToggle, NavDesktopLinks } from "@/pages/LandingPage";
import { api } from "@/lib/api";
import { Mail, Send, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactUs() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Contact us | Ez Health";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      setError("Please enter a message before sending.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await api.post(`${BACKEND_URL}/api/contact-us`, {
        message: message.trim(),
        // Only send email if the visitor typed one — backend treats it as optional
        email: email.trim() || undefined,
      });
      setSent(true);
    } catch (err) {
      // Surface backend's detail when present; otherwise generic fallback.
      const detail = err?.response?.data?.detail;
      setError(detail || "Could not send your message — please try again or email jobs@ez.health directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="contact-us-page">
      {/* Header — matches the rest of the Guide funnel */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 sm:gap-7">
            <NavMobileToggle />
            <a
              href="https://careers.ez.health"
              data-testid="contact-header-logo-link"
              aria-label="Ez Health careers home"
              className="inline-flex items-center"
            >
              <EzLogo size="h-9" />
            </a>
            <NavDesktopLinks />
          </div>
        </div>
      </header>

      <main className="flex-1">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#EDF5FB] to-white" />
          <div className="relative max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8 lg:pt-16 lg:pb-10 text-center">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, type: "spring", stiffness: 160, damping: 18 }}
              className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-ez-blue/10 text-ez-blue mb-5"
            >
              <Mail className="w-7 h-7" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ez-teal font-heading leading-tight mb-4"
              data-testid="contact-us-title"
            >
              Contact us
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-base md:text-lg text-slate-600 leading-relaxed font-body"
            >
              Have a question about our jobs or anything else on this site? Fill out the
              form below, or email us at{" "}
              <a
                href="mailto:jobs@ez.health"
                className="text-ez-blue hover:underline font-semibold"
              >
                jobs@ez.health
              </a>
              .
            </motion.p>
          </div>
        </section>

        <section className="pb-16 lg:pb-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-8 text-center"
                data-testid="contact-us-success"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 mb-4">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h2 className="text-xl font-bold text-ez-teal font-heading mb-2">Message sent</h2>
                <p className="text-slate-600 font-body mb-6">
                  Thanks for reaching out — we'll get back to you shortly.
                </p>
                <Button
                  data-testid="contact-back-home"
                  onClick={() => navigate("/")}
                  className="rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] px-6 py-2.5 font-semibold"
                >
                  Back to home
                </Button>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 lg:p-8 space-y-5"
                data-testid="contact-us-form"
              >
                <div>
                  <label
                    htmlFor="contact-email"
                    className="block text-sm font-semibold text-ez-teal font-heading mb-2"
                  >
                    Your email <span className="text-slate-400 font-normal">(optional, so we can reply)</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 font-body focus:outline-none focus:ring-2 focus:ring-ez-blue/30 focus:border-ez-blue transition-colors"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-message"
                    className="block text-sm font-semibold text-ez-teal font-heading mb-2"
                  >
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={8}
                    maxLength={5000}
                    placeholder="What would you like to ask us?"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-800 font-body resize-y focus:outline-none focus:ring-2 focus:ring-ez-blue/30 focus:border-ez-blue transition-colors"
                    data-testid="contact-message-input"
                  />
                  <p className="text-xs text-slate-400 font-body mt-1.5">
                    {message.length}/5000
                  </p>
                </div>

                {error && (
                  <p className="text-sm text-rose-600 font-body" data-testid="contact-error">
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={submitting || !message.trim()}
                  className="w-full sm:w-auto rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] disabled:opacity-50 disabled:cursor-not-allowed px-8 py-3 font-semibold shadow-lg shadow-blue-100"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? "Sending…" : <>Send message <Send className="w-4 h-4 ml-2" /></>}
                </Button>
              </form>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
